import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Network, Plus, Pencil, Trash2, FileText, ImageIcon, Download, Loader2, Inbox, ExternalLink, Newspaper, Mail, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { api, fileUrl } from '../../lib/api';
import { ImageField } from './AdminCrud';

const BADGES = { supports: 'Supports the Tracker', shares: 'Shares the Tracker', embeds: 'Embeds the Tracker', uses_live: 'Uses the Tracker Live', network_member: 'Network Member' };
const OFFICES = { ward: 'Ward Councillor', mayor: 'Mayor', trustee: 'Trustee' };
const EMPTY_SUP = { name: '', office: 'ward', ward: '', slug: '', photo_url: '', badge: 'supports', website_url: '', tracker_url: '', embed_live: false, statement: '', public_listed: false, approved: false, contact_email: '', notes: '' };

const REPORT_TYPES = {
  monthly_ward: 'Monthly Ward Issue Report',
  citywide: 'Citywide Public Issue Report',
  cross_ward_trends: 'Cross-Ward Trend Report',
  repeat_areas: 'Repeated Problem Areas Report',
  response_time: 'Response Time / Follow-Up Report',
  participation: 'Candidate Participation Report',
  network: 'Public Issue Tracker Network Report',
};

const CARD_TEMPLATES = [
  { id: 'support', headline: 'I Support the Public Issue Tracker', sub: 'Report it. Map it. Track it. Get answers.' },
  { id: 'my_ward', headline: 'My Ward Uses the Public Issue Tracker', sub: 'Report it. Map it. Track it. Get answers.' },
  { id: 'tagline', headline: 'Report it. Map it. Track it. Get answers.', sub: 'One complaint can be overlooked. A map full of complaints cannot.' },
  { id: 'public', headline: 'Public problems need public accountability.', sub: 'Powered by the Public Issue Tracker' },
  { id: 'powered', headline: 'Powered by the Public Issue Tracker', sub: 'By Steve Lesperance, Ward 8' },
];
const CARD_SIZES = { square: [1080, 1080], story: [1080, 1920], landscape: [1200, 630] };
const CARD_BGS = [
  { label: 'Ward 8 Aerial', url: '/api/uploads/ward8-aerial.jpg' },
  { label: 'Rural Road', url: '/api/uploads/885071e5-9d48-420f-91b8-0a6c556566f4.jpg' },
  { label: 'Council Chamber', url: '/api/uploads/council-chamber-neutral-1.jpg' },
  { label: 'Navy (no photo)', url: 'none' },
];

// ---------------------------------------------------------------- join requests tab
const JOIN_STATUSES = { new: 'New', needs_review: 'Needs Review', approved: 'Approved', declined: 'Declined', contacted: 'Contacted' };
const JOIN_STATUS_STYLES = {
  new: 'bg-blue-50 text-blue-800 border-blue-200',
  needs_review: 'bg-amber-50 text-amber-800 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  declined: 'bg-red-50 text-red-700 border-red-200',
  contacted: 'bg-slate-100 text-slate-700 border-slate-300',
};
const JOIN_USAGE_LABELS = { direct_link: 'Direct link', embedded_widget: 'Embedded widget', ward_tracker: 'Ward tracker', report_access: 'Report access', not_sure: 'Not sure yet' };
const JOIN_OFFICE_LABELS = { ward: 'Ward Councillor', citywide: 'Citywide / team', mayor: 'Mayor' };

function JoinRequestsTab() {
  const [items, setItems] = useState(null);
  const load = () => api.get('/admin/network/join-requests').then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (item, status) => {
    try {
      await api.put(`/admin/network/join-requests/${item.id}`, { status });
      toast.success(`Marked as ${JOIN_STATUSES[status]}`); load();
    } catch { toast.error('Update failed'); }
  };
  const remove = async (item) => {
    if (!window.confirm(`Delete request from ${item.name}?`)) return;
    await api.delete(`/admin/network/join-requests/${item.id}`); toast.success('Deleted'); load();
  };

  if (items === null) return <p className="text-sm text-slate-400 py-6">Loading…</p>;
  return (
    <div className="space-y-4">
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-600 leading-relaxed" data-testid="join-requests-note">
        Join requests are <strong>private</strong>. Approving a request here does NOT list anyone publicly — to list a supporter, add them on the Supporters tab and turn on both Approved and Publicly Listed with their permission.
      </div>
      {items.length === 0 && <Card><CardContent className="p-8 text-center text-sm text-slate-500" data-testid="join-requests-empty">No join requests yet. Requests from /tracker-network/join appear here.</CardContent></Card>}
      {items.map((r) => (
        <Card key={r.id} data-testid={`join-request-${r.id}`}>
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-bold text-[#1a2744]">{r.name}</p>
                  <Badge variant="outline" className={`text-[10px] font-bold ${JOIN_STATUS_STYLES[r.status]}`}>{JOIN_STATUSES[r.status]}</Badge>
                  <Badge variant="outline" className="text-[10px] font-bold border-slate-300 text-slate-600">{JOIN_OFFICE_LABELS[r.office]}{r.ward ? ` · Ward ${r.ward}` : ''}</Badge>
                </div>
                <p className="text-xs text-slate-500 mt-1">{r.email}{r.phone ? ` · ${r.phone}` : ''} · {(r.created_at || '').slice(0, 10)}</p>
                {(r.usage || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {r.usage.map((u) => <Badge key={u} variant="outline" className="text-[10px] border-[#C9A84C] text-[#7A5C12] bg-[#C9A84C]/10 font-bold">{JOIN_USAGE_LABELS[u] || u}</Badge>)}
                  </div>
                )}
                {r.message && <p className="text-sm text-slate-600 mt-2 leading-relaxed border-l-2 border-slate-200 pl-3">{r.message}</p>}
                <div className="flex flex-wrap gap-3 mt-2">
                  {r.website_url && <a href={r.website_url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[#1a2744] underline underline-offset-2 inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Website</a>}
                  {r.facebook_url && <a href={r.facebook_url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[#1a2744] underline underline-offset-2 inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Facebook</a>}
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0 w-full lg:w-52">
                <Select value={r.status} onValueChange={(v) => setStatus(r, v)}>
                  <SelectTrigger className="h-9 text-xs" data-testid={`join-status-select-${r.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(JOIN_STATUSES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => remove(r)} className="h-9 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`join-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- supporters tab
function SupportersTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get('/admin/network/supporters').then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const save = async () => {
    if (!editing.name.trim()) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      if (editing.id) {
        const { id, created_at, updated_at, ...payload } = editing;
        await api.put(`/admin/network/supporters/${id}`, payload);
      } else {
        await api.post('/admin/network/supporters', editing);
      }
      toast.success('Saved'); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };
  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    await api.delete(`/admin/network/supporters/${item.id}`); toast.success('Deleted'); load();
  };

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 leading-relaxed" data-testid="supporters-optin-warning">
        Opt-in rule: a candidate appears publicly ONLY when both <strong>Approved</strong> and <strong>Publicly listed</strong> are turned on. Never enable public listing without the candidate's explicit permission.
      </div>
      <div className="flex justify-end"><Button onClick={() => setEditing({ ...EMPTY_SUP })} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold" data-testid="supporter-new-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Candidate</Button></div>
      {items.length === 0 && <Card><CardContent className="p-8 text-center text-sm text-slate-500" data-testid="supporters-admin-empty">No candidates in the network yet.</CardContent></Card>}
      {items.map((s) => (
        <Card key={s.id} data-testid={`supporter-admin-${s.id}`}>
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-bold text-[#1a2744]">{s.name}</p>
                <Badge variant="outline" className="text-[10px] font-bold border-slate-300 text-slate-600">{OFFICES[s.office]}{s.ward ? ` · Ward ${s.ward}` : ''}</Badge>
                <Badge variant="outline" className="text-[10px] font-bold border-[#C9A84C] text-[#7A5C12] bg-[#C9A84C]/10">{BADGES[s.badge]}</Badge>
                {s.approved ? <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px]">Approved</Badge> : <Badge className="bg-slate-100 text-slate-600 border border-slate-300 text-[10px]">Not approved</Badge>}
                {s.public_listed ? <Badge className="bg-blue-50 text-blue-800 border border-blue-200 text-[10px]">Publicly listed</Badge> : <Badge className="bg-slate-100 text-slate-600 border border-slate-300 text-[10px]">Hidden</Badge>}
              </div>
              {s.slug && <p className="text-xs text-slate-500 mt-1 font-mono">/tracker/{s.slug}</p>}
            </div>
            <div className="flex gap-1.5 shrink-0">
              <Button size="sm" variant="outline" onClick={() => setEditing({ ...EMPTY_SUP, ...s })} className="h-9" data-testid={`supporter-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" onClick={() => remove(s)} className="h-9 text-red-600 border-red-200 hover:bg-red-50" data-testid={`supporter-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </CardContent>
        </Card>
      ))}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="supporter-dialog">
          <DialogHeader><DialogTitle className="font-serif">{editing?.id ? 'Edit candidate' : 'Add candidate'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><label className="block text-xs font-bold text-slate-600 mb-1">Candidate name</label><Input value={editing.name} onChange={(e) => set('name', e.target.value)} className="h-10" data-testid="supporter-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Office sought</label>
                  <Select value={editing.office} onValueChange={(v) => set('office', v)}>
                    <SelectTrigger className="h-10" data-testid="supporter-office-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(OFFICES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Ward #</label><Input value={editing.ward} onChange={(e) => set('ward', e.target.value)} className="h-10" data-testid="supporter-ward-input" /></div>
              </div>
              <div><label className="block text-xs font-bold text-slate-600 mb-1">Hosted tracker slug (e.g. ward-1)</label><Input value={editing.slug} onChange={(e) => set('slug', e.target.value)} className="h-10 font-mono" data-testid="supporter-slug-input" /></div>
              <div><label className="block text-xs font-bold text-slate-600 mb-1">Support badge</label>
                <Select value={editing.badge} onValueChange={(v) => set('badge', v)}>
                  <SelectTrigger className="h-10" data-testid="supporter-badge-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(BADGES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="block text-xs font-bold text-slate-600 mb-1">Photo / logo</label><ImageField value={editing.photo_url} onChange={(v) => set('photo_url', v)} category="network" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Website link</label><Input value={editing.website_url} onChange={(e) => set('website_url', e.target.value)} className="h-10" data-testid="supporter-website-input" /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Tracker link (optional)</label><Input value={editing.tracker_url} onChange={(e) => set('tracker_url', e.target.value)} className="h-10" /></div>
              </div>
              <div><label className="block text-xs font-bold text-slate-600 mb-1">Statement of support (short)</label><Textarea value={editing.statement} onChange={(e) => set('statement', e.target.value)} rows={2} data-testid="supporter-statement-input" /></div>
              <div className="grid grid-cols-1 gap-2 border-t pt-3">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Switch checked={editing.embed_live} onCheckedChange={(v) => set('embed_live', v)} data-testid="supporter-live-toggle" /> Embed / tracker is live</label>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Switch checked={editing.approved} onCheckedChange={(v) => set('approved', v)} data-testid="supporter-approved-toggle" /> Approved by Steve / team</label>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Switch checked={editing.public_listed} onCheckedChange={(v) => set('public_listed', v)} data-testid="supporter-public-toggle" /> Publicly listed (candidate opted in)</label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Contact email (private)</label><Input value={editing.contact_email} onChange={(e) => set('contact_email', e.target.value)} className="h-10" /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Internal notes (private)</label><Input value={editing.notes} onChange={(e) => set('notes', e.target.value)} className="h-10" /></div>
              </div>
              <Button onClick={save} disabled={busy} className="w-full h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold" data-testid="supporter-save-btn">{busy ? 'Saving…' : 'Save'}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------- reports tab
function ReportRow({ rep, onChanged }) {
  const [busy, setBusy] = useState('');
  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === 'pdf') {
        const r = await api.post(`/admin/network/reports/${rep.id}/pdf`);
        toast.success('PDF generated'); onChanged();
        window.open(fileUrl(r.data.pdf_url), '_blank');
      } else if (kind === 'newsroom') {
        await api.post(`/admin/network/reports/${rep.id}/publish-link`);
        toast.success('PDF link added to the draft article — publish manually from Admin → Newsroom');
      } else if (kind === 'newsletter') {
        await api.post(`/admin/network/reports/${rep.id}/to-newsletter`);
        toast.success('Draft newsletter created — nothing is sent automatically');
      } else if (kind === 'delete') {
        if (!window.confirm(`Delete stored report "${rep.title}"? (The draft article is not deleted.)`)) { setBusy(''); return; }
        await api.delete(`/admin/network/reports/${rep.id}`);
        toast.success('Report removed'); onChanged();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy('');
  };
  return (
    <div className="rounded-xl border border-slate-200 p-3" data-testid={`network-report-${rep.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-bold text-[#1a2744] text-sm">{rep.title}</p>
        {rep.pdf_url && <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px]">PDF ready</Badge>}
      </div>
      <p className="text-xs text-slate-500 mt-0.5">{rep.scope} · {rep.period} · {rep.stats?.total ?? 0} tracked · updated {(rep.updated_at || '').slice(0, 10)}</p>
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {rep.pdf_url && (
          <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid={`report-preview-${rep.id}`}>
            <a href={fileUrl(rep.pdf_url)} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3 w-3 mr-1" /> Preview / Download</a>
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => act('pdf')} disabled={!!busy} className="h-8 text-xs font-bold" data-testid={`report-pdf-${rep.id}`}>
          {busy === 'pdf' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />} {rep.pdf_url ? 'Regenerate PDF' : 'Generate PDF'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => act('newsroom')} disabled={!!busy || !rep.pdf_url} className="h-8 text-xs font-bold" data-testid={`report-newsroom-${rep.id}`}>
          {busy === 'newsroom' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Newspaper className="h-3 w-3 mr-1" />} PDF Link → Newsroom Draft
        </Button>
        <Button size="sm" variant="outline" onClick={() => act('newsletter')} disabled={!!busy} className="h-8 text-xs font-bold" data-testid={`report-newsletter-${rep.id}`}>
          {busy === 'newsletter' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Mail className="h-3 w-3 mr-1" />} Attach → Newsletter Draft
        </Button>
        <Button size="sm" variant="outline" onClick={() => act('delete')} disabled={!!busy} className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`report-delete-${rep.id}`}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function ReportsTab() {
  const [form, setForm] = useState({ report_type: 'monthly_ward', tracker_slug: 'ward-8', date_from: '', date_to: '', use_ai: true });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [reports, setReports] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadReports = () => api.get('/admin/network/reports').then((r) => setReports(r.data || [])).catch(() => {});
  useEffect(() => { loadReports(); }, []);

  const generate = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await api.post('/admin/network/report', form);
      setResult(r.data);
      toast.success('Draft report created in the Newsroom');
      loadReports();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      <Card><CardContent className="p-5 space-y-4">
      <p className="text-sm text-slate-600">Generates a <strong>privacy-safe draft Newsroom article</strong> from tracker data (aggregate numbers only — no names, addresses, or contact info). Reports are never auto-published: review and publish manually.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className="block text-xs font-bold text-slate-600 mb-1">Report type</label>
          <Select value={form.report_type} onValueChange={(v) => set('report_type', v)}>
            <SelectTrigger className="h-10" data-testid="report-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(REPORT_TYPES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><label className="block text-xs font-bold text-slate-600 mb-1">Scope</label>
          <Select value={form.tracker_slug} onValueChange={(v) => set('tracker_slug', v)}>
            <SelectTrigger className="h-10" data-testid="report-scope-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ward-8">Ward 8 only</SelectItem>
              <SelectItem value="all">All participating wards (citywide)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><label className="block text-xs font-bold text-slate-600 mb-1">From date</label><Input type="date" value={form.date_from} onChange={(e) => set('date_from', e.target.value)} className="h-10" data-testid="report-from-input" /></div>
        <div><label className="block text-xs font-bold text-slate-600 mb-1">To date</label><Input type="date" value={form.date_to} onChange={(e) => set('date_to', e.target.value)} className="h-10" data-testid="report-to-input" /></div>
      </div>
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Switch checked={form.use_ai} onCheckedChange={(v) => set('use_ai', v)} data-testid="report-ai-toggle" /> Add AI plain-language explainer</label>
      <Button onClick={generate} disabled={busy} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold" data-testid="report-generate-btn">
        {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Generating…</> : <><FileText className="h-4 w-4 mr-1.5" /> Generate Draft Report</>}
      </Button>
      {result && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm" data-testid="report-result">
          <p className="font-bold text-emerald-900">Draft created — review it in Admin → Newsroom before publishing.</p>
          <p className="text-emerald-800 mt-1">{result.stats.total} tracked · {result.stats.resolved} resolved · {result.stats.open} open · avg {result.stats.avg_days_open} days open{result.ai_used ? ' · AI explainer included' : ''}</p>
        </div>
      )}
      </CardContent></Card>

      <div>
        <p className="font-serif font-bold text-[#1a2744] text-sm mb-2">Generated reports & PDFs</p>
        <p className="text-xs text-slate-500 mb-3">PDFs are privacy-safe: aggregate numbers and public links only — never names, contact details, addresses, notes, or files. Nothing publishes automatically.</p>
        {reports.length === 0 ? (
          <Card><CardContent className="p-6 text-center text-sm text-slate-500" data-testid="network-reports-empty">No generated reports yet. Generate one above to enable PDF export.</CardContent></Card>
        ) : (
          <div className="space-y-2.5" data-testid="network-reports-list">
            {reports.map((rep) => <ReportRow key={rep.id} rep={rep} onChanged={loadReports} />)}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- share cards tab
function ShareCardsTab() {
  const [tpl, setTpl] = useState(CARD_TEMPLATES[0].id);
  const [size, setSize] = useState('square');
  const [bg, setBg] = useState(CARD_BGS[0].url);
  const [fields, setFields] = useState({ candidate: 'Steve Lesperance', ward: '8', link: 'SteveLesperance.com', headline: CARD_TEMPLATES[0].headline, sub: CARD_TEMPLATES[0].sub });
  const canvasRef = useRef(null);
  const [rendering, setRendering] = useState(false);
  const set = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const pickTemplate = (id) => {
    setTpl(id);
    const t = CARD_TEMPLATES.find((x) => x.id === id);
    setFields((f) => ({ ...f, headline: t.headline, sub: t.sub }));
  };

  const wrapText = (ctx, text, x, y, maxW, lh) => {
    const words = text.split(' ');
    let line = '', yy = y;
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (ctx.measureText(test).width > maxW && line) { ctx.fillText(line, x, yy); line = w; yy += lh; }
      else line = test;
    }
    if (line) ctx.fillText(line, x, yy);
    return yy + lh;
  };

  const render = useCallback(async (download = false) => {
    setRendering(true);
    try {
      const [W, H] = CARD_SIZES[size];
      const canvas = canvasRef.current;
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d');
      // background
      ctx.fillStyle = '#141f36'; ctx.fillRect(0, 0, W, H);
      if (bg && bg !== 'none') {
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = fileUrl(bg); });
          const scale = Math.max(W / img.width, H / img.height);
          const iw = img.width * scale, ih = img.height * scale;
          ctx.drawImage(img, (W - iw) / 2, (H - ih) / 2, iw, ih);
          ctx.fillStyle = 'rgba(20,31,54,0.72)'; ctx.fillRect(0, 0, W, H);
        } catch (e) {
          // Background image failed to load — fall back to the navy fill.
        }
      }
      // gold rule
      const pad = Math.round(W * 0.08);
      ctx.fillStyle = '#C9A84C'; ctx.fillRect(pad, Math.round(H * 0.16), Math.round(W * 0.12), Math.max(6, Math.round(H * 0.008)));
      // headline
      ctx.fillStyle = '#ffffff';
      const hSize = Math.round(W * (size === 'story' ? 0.075 : 0.06));
      ctx.font = `900 ${hSize}px Georgia, serif`;
      let y = wrapText(ctx, fields.headline, pad, Math.round(H * 0.16) + hSize + 24, W - pad * 2, hSize * 1.15);
      // sub
      ctx.fillStyle = '#C9A84C';
      const sSize = Math.round(hSize * 0.5);
      ctx.font = `bold ${sSize}px Georgia, serif`;
      y = wrapText(ctx, fields.sub, pad, y + sSize * 0.5, W - pad * 2, sSize * 1.3);
      // candidate + ward
      const who = [fields.candidate, fields.ward ? `Ward ${fields.ward}` : ''].filter(Boolean).join(' · ');
      ctx.fillStyle = '#ffffff';
      const wSize = Math.round(sSize * 0.85);
      ctx.font = `bold ${wSize}px Arial, sans-serif`;
      ctx.fillText(who, pad, H - pad - wSize * 1.8);
      // link
      ctx.fillStyle = '#C9A84C';
      ctx.font = `bold ${Math.round(wSize * 0.9)}px Arial, sans-serif`;
      ctx.fillText(fields.link, pad, H - pad);
      if (download) {
        const a = document.createElement('a');
        a.download = `tracker-card-${tpl}-${size}.png`;
        a.href = canvas.toDataURL('image/png');
        a.click();
        toast.success('Share card downloaded');
      }
    } catch (e) { toast.error('Render failed'); }
    setRendering(false);
  }, [tpl, size, bg, fields]);

  useEffect(() => { render(false); }, [render]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card><CardContent className="p-5 space-y-3">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 leading-relaxed" data-testid="sharecard-warning">
          Approval rule: never create or publish candidate-branded graphics for someone who has not opted in. All exports are manual — nothing is auto-published.
        </div>
        <div><label className="block text-xs font-bold text-slate-600 mb-1">Template</label>
          <Select value={tpl} onValueChange={pickTemplate}>
            <SelectTrigger className="h-10" data-testid="sharecard-template-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CARD_TEMPLATES.map((t) => <SelectItem key={t.id} value={t.id}>{t.headline}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-bold text-slate-600 mb-1">Size</label>
            <Select value={size} onValueChange={setSize}>
              <SelectTrigger className="h-10" data-testid="sharecard-size-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="square">Square post (1080×1080)</SelectItem><SelectItem value="story">Story (1080×1920)</SelectItem><SelectItem value="landscape">Landscape (1200×630)</SelectItem></SelectContent>
            </Select>
          </div>
          <div><label className="block text-xs font-bold text-slate-600 mb-1">Background</label>
            <Select value={bg} onValueChange={setBg}>
              <SelectTrigger className="h-10" data-testid="sharecard-bg-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CARD_BGS.map((b) => <SelectItem key={b.label} value={b.url}>{b.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div><label className="block text-xs font-bold text-slate-600 mb-1">Headline</label><Input value={fields.headline} onChange={(e) => set('headline', e.target.value)} className="h-10" data-testid="sharecard-headline-input" /></div>
        <div><label className="block text-xs font-bold text-slate-600 mb-1">Subheadline</label><Input value={fields.sub} onChange={(e) => set('sub', e.target.value)} className="h-10" data-testid="sharecard-sub-input" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-bold text-slate-600 mb-1">Candidate name</label><Input value={fields.candidate} onChange={(e) => set('candidate', e.target.value)} className="h-10" data-testid="sharecard-candidate-input" /></div>
          <div><label className="block text-xs font-bold text-slate-600 mb-1">Ward #</label><Input value={fields.ward} onChange={(e) => set('ward', e.target.value)} className="h-10" data-testid="sharecard-ward-input" /></div>
        </div>
        <div><label className="block text-xs font-bold text-slate-600 mb-1">Link / URL text</label><Input value={fields.link} onChange={(e) => set('link', e.target.value)} className="h-10" data-testid="sharecard-link-input" /></div>
        <Button onClick={() => render(true)} disabled={rendering} className="h-11 w-full bg-[#1a2744] hover:bg-[#243456] text-white font-bold" data-testid="sharecard-download-btn">
          <Download className="h-4 w-4 mr-1.5" /> Download PNG
        </Button>
      </CardContent></Card>
      <Card><CardContent className="p-5">
        <p className="text-xs font-bold text-slate-600 mb-2 flex items-center gap-1.5"><ImageIcon className="h-4 w-4 text-[#C9A84C]" /> Live preview</p>
        <canvas ref={canvasRef} className="w-full h-auto rounded-xl ring-1 ring-slate-200" data-testid="sharecard-canvas" />
      </CardContent></Card>
    </div>
  );
}

export default function AdminNetwork() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = ['supporters', 'join', 'reports', 'cards'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'supporters';
  return (
    <div className="p-4 sm:p-6 max-w-5xl" data-testid="admin-network-page">
      <h1 className="font-serif font-black text-[#1a2744] text-2xl flex items-center gap-2"><Network className="h-6 w-6 text-[#C9A84C]" /> Tracker Network</h1>
      <p className="text-sm text-slate-500 mt-1">Supporters, network reports, and share graphics for the Public Issue Tracker Network. Everything here is approval-based — nothing publishes automatically.</p>
      <Tabs value={tab} onValueChange={(v) => setSearchParams({ tab: v }, { replace: true })} className="mt-5">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="supporters" data-testid="network-tab-supporters">Supporters</TabsTrigger>
          <TabsTrigger value="join" data-testid="network-tab-join">Join Requests</TabsTrigger>
          <TabsTrigger value="reports" data-testid="network-tab-reports">Network Reports</TabsTrigger>
          <TabsTrigger value="cards" data-testid="network-tab-cards">Share Cards</TabsTrigger>
        </TabsList>
        <TabsContent value="supporters" className="mt-4"><SupportersTab /></TabsContent>
        <TabsContent value="join" className="mt-4"><JoinRequestsTab /></TabsContent>
        <TabsContent value="reports" className="mt-4"><ReportsTab /></TabsContent>
        <TabsContent value="cards" className="mt-4"><ShareCardsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
